import {
  Globe,
  FileText,
  Database,
  Cloud,
  Phone,
  MessageCircle,
  MessageSquare,
  Megaphone,
  TrendingUp,
  ShoppingCart,
  Headphones,
  ArrowRight,
} from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const dataSourceIcons = [Globe, FileText, Database, Cloud] as const;
const agentMeta = [
  { icon: Megaphone, color: "bg-chathero-mint" },
  { icon: TrendingUp, color: "bg-chathero-orange" },
  { icon: ShoppingCart, color: "bg-secondary" },
  { icon: Headphones, color: "bg-chathero-mint" },
] as const;
const channelIcons = [Phone, MessageCircle, MessageSquare] as const;

const PlatformVisualizationSection = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const { t, tArray } = useLanguage();
  const dataSources = tArray("knowledgeBasePage.visualization.dataSourceItems");
  const channels = ["Voice", "Chat", "WhatsApp"];
  const aiAgents = ["Marketing", "Sales", "Commerce", "Service"];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 md:py-24 bg-secondary/30 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div
            className={cn(
              "text-center mb-12 transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <div className="flex justify-center mb-4">
              <Badge variant="outline" className="text-sm font-semibold uppercase tracking-wider">
                {t("knowledgeBasePage.visualization.badge")}
              </Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              {t("knowledgeBasePage.visualization.title")}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("knowledgeBasePage.visualization.intro")}
            </p>
          </div>

          <div className="hidden md:flex items-stretch justify-center gap-4 lg:gap-6">
            <div
              className={cn(
                "flex-1 max-w-[180px] transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <div className="bg-card rounded-2xl p-5 border border-border shadow-sm h-full">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-4">
                  {t("knowledgeBasePage.visualization.dataSources")}
                </p>
                <div className="space-y-3">
                  {dataSources.map((source, idx) => {
                    const Icon = dataSourceIcons[idx];
                    return (
                      <div key={source} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                          <Icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm text-muted-foreground">{source}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={cn("flex items-center transition-all duration-500", isVisible ? "opacity-100" : "opacity-0")} style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}>
              <ArrowRight className="w-6 h-6 text-primary" />
            </div>

            <div
              className={cn(
                "flex-1 max-w-sm transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              )}
              style={{ transitionDelay: isVisible ? "300ms" : "0ms" }}
            >
              <div className="bg-chathero-mint/10 rounded-2xl p-6 border-2 border-chathero-mint/30 shadow-lg relative h-full">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-chathero-mint text-primary px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  {t("knowledgeBasePage.visualization.central")}
                </div>

                <h3 className="text-lg font-bold text-foreground text-center mb-4 mt-2">
                  {t("knowledgeBasePage.visualization.kbTitle")}
                </h3>

                <div className="space-y-3 mb-4">
                  <div className="bg-chathero-mint/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-chathero-mint/30 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-chathero-mint" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{t("knowledgeBasePage.visualization.contentKnowledge")}</p>
                        <p className="text-xs text-muted-foreground">{t("knowledgeBasePage.visualization.contentKnowledgeDesc")}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-chathero-mint/10 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-chathero-orange/30 flex items-center justify-center">
                        <Database className="w-4 h-4 text-chathero-orange" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{t("knowledgeBasePage.visualization.productKnowledge")}</p>
                        <p className="text-xs text-muted-foreground">{t("knowledgeBasePage.visualization.productKnowledgeDesc")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground">{t("knowledgeBasePage.visualization.structuralNote")}</p>
              </div>
            </div>

            <div className={cn("flex items-center transition-all duration-500", isVisible ? "opacity-100" : "opacity-0")} style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}>
              <ArrowRight className="w-6 h-6 text-primary" />
            </div>

            <div
              className={cn(
                "flex-1 max-w-[180px] transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
            >
              <div className="bg-card rounded-2xl p-5 border border-border shadow-sm h-full">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-4">AI Agents</p>
                <div className="space-y-3">
                  {aiAgents.map((agent, idx) => {
                    const meta = agentMeta[idx];
                    return (
                      <div key={agent} className="flex items-center gap-3">
                        <div className={cn("w-9 h-9 rounded-full flex items-center justify-center", meta.color + "/30")}>
                          <meta.icon className={cn("w-4 h-4", meta.color === "bg-chathero-mint" ? "text-chathero-mint" : meta.color === "bg-chathero-orange" ? "text-chathero-orange" : "text-primary")} />
                        </div>
                        <span className="text-sm text-muted-foreground">{agent}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "hidden md:flex flex-col items-center gap-3 mt-8 transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-sm text-muted-foreground">{t("knowledgeBasePage.visualization.outputVia")}</p>
            <div className="flex justify-center gap-6">
              {channels.map((channel, idx) => {
                const Icon = channelIcons[idx];
                return (
                  <div key={channel} className="flex items-center gap-2 bg-secondary rounded-lg px-4 py-2">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground">{channel}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:hidden flex flex-col items-center gap-4">
            <div className={cn("w-full max-w-xs transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
              <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3">
                  {t("knowledgeBasePage.visualization.dataSources")}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {dataSources.map((source, idx) => {
                    const Icon = dataSourceIcons[idx];
                    return (
                      <div key={source} className="flex flex-col items-center gap-1">
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                          <Icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">{source}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <ArrowRight className="w-5 h-5 text-primary rotate-90" />

            <div className={cn("w-full max-w-xs transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}>
              <div className="bg-chathero-mint/10 rounded-xl p-5 border-2 border-chathero-mint/30 shadow-lg relative">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-chathero-mint text-primary px-3 py-0.5 rounded-full text-xs font-bold">
                  {t("knowledgeBasePage.visualization.central")}
                </div>
                <h3 className="text-base font-bold text-foreground text-center mb-3 mt-1">{t("knowledgeBasePage.visualization.kbTitle")}</h3>
                <div className="flex gap-2">
                  <div className="flex-1 bg-chathero-mint/10 rounded-lg p-3 text-center">
                    <FileText className="w-4 h-4 text-chathero-mint mx-auto mb-1" />
                    <p className="text-xs text-foreground">Content</p>
                  </div>
                  <div className="flex-1 bg-chathero-mint/10 rounded-lg p-3 text-center">
                    <Database className="w-4 h-4 text-chathero-orange mx-auto mb-1" />
                    <p className="text-xs text-foreground">Product</p>
                  </div>
                </div>
              </div>
            </div>

            <ArrowRight className="w-5 h-5 text-primary rotate-90" />

            <div className={cn("w-full max-w-xs transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}>
              <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3">AI Agents</p>
                <div className="grid grid-cols-4 gap-2">
                  {aiAgents.map((agent, idx) => {
                    const meta = agentMeta[idx];
                    return (
                      <div key={agent} className="flex flex-col items-center gap-1">
                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", meta.color + "/30")}>
                          <meta.icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">{agent}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-2">
              {channels.map((channel, idx) => {
                const Icon = channelIcons[idx];
                return (
                  <div key={channel} className="flex items-center gap-1.5 bg-secondary rounded-lg px-3 py-1.5">
                    <Icon className="w-3 h-3 text-primary" />
                    <span className="text-xs text-foreground">{channel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformVisualizationSection;
